import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useCtaColorVars } from '../hooks/useCtaColorVars';

// ============================================================================
// Meet The Team — Intro
// ---------------------------------------------------------------------------
// A short lead paragraph that sits directly below the page hero and sets the
// "welcoming professionals ready to assist you" tone from the Fuze Woodstock
// reference. Visually mirrors the BetterBuiltIntro header (centered Oswald
// title + Space Mono lead paragraph) so the page feels native to the theme.
// ============================================================================

export function MeetTheTeamIntro() {
  useRevealStyles();
  const introRef = useScrollReveal<HTMLDivElement>();
  const ctaColorVars = useCtaColorVars();

  return (
    <section className="mtti-section" style={ctaColorVars}>
      <div className="mtti-container">
        <div ref={introRef} className="reveal-fade-up mtti-intro">
          <RichTextContent
            path="meettheteamintro.heading"
            label="Heading"
            defaultValue="<p>Here to Help You Feel at Home</p>"
            as="h2"
            className="mtti-heading"
          />
          <span aria-hidden="true" className="mtti-accent" />
        </div>
      </div>

      <style>{`
        .mtti-section {
          position: relative;
          background: var(--vibe-color-background, #ffffff);
          padding: 3rem 1.25rem 2rem;
        }
        @media (min-width: 768px) {
          .mtti-section { padding: 4.5rem 1.5rem 2.5rem; }
        }

        .mtti-container {
          max-width: 820px;
          margin: 0 auto;
        }

        .mtti-intro {
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        /* Scaled up to match the on-page title treatment used across the
           theme (HeroSolidBG hero title, BetterBuiltIntro heading): Oswald
           400, uppercase, tight line-height, wider tracking on larger
           viewports. The short accent bar below echoes the vertical accent
           pinned under the chevron hero. */
        .mtti-heading,
        .mtti-heading * {
          font-family: var(--vibe-font-heading, 'Oswald', 'Arial Narrow', system-ui, sans-serif);
          font-weight: 400;
        }
        .mtti-heading {
          margin: 0;
          font-size: clamp(2rem, 5vw, 3.25rem);
          line-height: 1.05;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          color: var(--vibe-color-secondary, #3E441D);
          text-wrap: balance;
          max-width: 24ch;
        }

        .mtti-accent {
          display: block;
          width: 3rem;
          height: 3px;
          margin-top: 1.25rem;
          background: var(--cta-bg, var(--vibe-color-secondary, #3E441D));
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'MeetTheTeamIntro',
  displayName: 'Meet The Team — Intro',
  tokens: {
    'meettheteamintro.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Here to Help You Feel at Home</p>',
    },
  },
};

(MeetTheTeamIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default MeetTheTeamIntro;
